<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';
	import Reviews from '$lib/PageSections/Reviews/Reviews.svelte';

	import dealerProgramContractorDiscountImg from './images/dealerProgramContractorDiscount.jpg';
	import indowWindowsInsertBedroom from './images/indow-windows-insert-bedroom.png';
	import KarenVintageImg from './images/KarenVintage.png';
	import noiseReduction from './images/noise-reduction.jpg';
</script>

<Section>
	<p>
		Installing soundproofing materials can be a disruptive process, requiring
		changes to the walls, windows, or ceiling. Soundproofing curtains are
		becoming an increasingly popular alternative, but are they the best choice
		for you?
	</p>
	<p>
		If you haven’t considered soundproofing curtains before, there are a few
		things you should know before purchasing. We’re going to break down how they
		work, their advantages and disadvantages, and an alternative solution, so
		you can make the best choice for your home.
	</p>
	<Image
		src={indowWindowsInsertBedroom}
		alt="bedroom with window curtains and indow inserts installed for soundproofing in san francisco"
		class="w-full"
	/>
	<h2>What Are Soundproof Curtains?</h2>
	<p>
		Soundproof curtains can also be known as sound-absorbing curtains, sound
		deadening curtains, acoustic curtains, and sound dampening curtains
		depending on the manufacturer you purchase from and the type of
		soundproofing you are trying to accomplish.
	</p>
	<p>
		Regardless of the name, these curtains are made of heavy, thick materials
		like polyester, velvet, or suede that you hang above your window frame. They
		usually have a porous surface that allows them to absorb sound waves in
		order to make a room quieter.
	</p>
	<p>
		A truly effective and high-quality soundproof curtain will be heavy, tightly
		woven, and run from ceiling to floor with several inches of fabric going
		past each side of the window. It’s essential they cover as much area around
		the window as possible so that the folds of the curtains can create a seal
		around the sides.
	</p>
	<p>
		However, though many manufacturers market curtains as soundproof, they are
		usually ineffective.
	</p>
</Section>
<Section>
	<Image
		alt="woman and child in home with window with curtains and indow inserts for street noise reduction"
		src={noiseReduction}
	/>
	<h2>Do Soundproof Curtains Work?</h2>
	<p>
		To answer this question, you need to know about the types of soundproofing.
	</p>
	<p>
		There are <a href="/resources/blog/most-effective-soundproofing-materials"
			>two main types of soundproofing</a
		>: sound reduction and sound absorption. Sound reduction stops or blocks
		sound from entering a space; using sound reduction will help keep your space
		free from outside noises such as weather, outdoor conversations, and
		traffic. Sound absorption, also called sound deadening, stops sound already
		inside a space from echoing and reverberating.
	</p>
	<p>
		According to a wide array of reviews, soundproof curtains are incredibly
		effective for sound deadening applications, but they are not useful as sound
		reduction. What does that mean? Sound deadening materials work by reducing
		vibrations to reduce echoes in a room, but they will not reduce the decibel
		level of sounds entering the room.
	</p>
	<p>
		Soundproof curtains are a simple additive to lessen the problem of
		overwhelming sounds, but they are not perfect problem solvers.
	</p>
</Section>
<Section>
	<h2>The Advantages of Soundproof Curtains</h2>
	<p>
		Soundproof curtains do contribute to noise reduction, keep a room from
		echoing, and provide a small amount of thermal insulation. While soundproof
		curtains should not be used to completely replace standard insulation in
		your walls and windows, they do contribute to added insulation for a warmer
		room in the winter and a cooler room in the summer.
	</p>
	<p>
		Because these types of curtains are made from heavy material, they are often
		thick and opaque, so if you are looking for increased privacy, they can be a
		decent solution. They are also fairly inexpensive, running around $50-100
		per window for good quality curtains.
	</p>
</Section>
<Section>
	<h2>The Disadvantages of Soundproof Curtains</h2>
	<p>Soundproof curtains have several disadvantages as well.</p>
	<p>
		They are not as effective as other noise reduction solutions, such as
		soundproof windows or noise reduction window inserts. Their thick, heavy
		nature makes them difficult to transport, hard to install, and challenging
		to wash and maintain.
	</p>
	<p>
		They can also make main living spaces too dark for comfort, limiting the
		number of rooms where they can be installed unless you are willing to pay
		increased electric bills and have a reduction in the amount of natural
		light.
	</p>
	<p>
		This is especially disadvantageous for people trying to soundproof during
		the day. If you need quiet in your office but want all of the <a
			href="https://sustainability.ncsu.edu/blog/changeyourstate/benefits-of-natural-light"
			target="_blank"
			rel="noreferrer">benefits of natural light</a
		> (such as increased productivity, better moods, etc.), a soundproof curtain
		won’t be able to work as effectively unless it’s completely closed, blocking
		out all of the light.
	</p>
</Section>
<Section>
	<Image
		alt="man installing window insert to block noise without blocking natural light"
		src={dealerProgramContractorDiscountImg}
	/>
	<h2>An Alternative: Indow Solutions That Can Help</h2>
	<p>
		Indow offers <a href="/resources/home-soundproofing"
			>window insert solutions</a
		> that are a perfect alternative to soundproof curtains, with a lower price point,
		easier installation, and an overall more comfortable experience.
	</p>
	<p>
		Just press custom-made <a href="/solutions/window-noise-reduction"
			>Indow inserts</a
		> into your window frames — with no mounting hardware or permanent damage — and
		enjoy your increased quiet. You’ll sleep better, feel better, and enjoy your
		space more. Noise-reducing windows have never been so easy to obtain.
	</p>
	<p>
		Our acrylic glazing edged in our patented silicone compression tubing
		creates a tight seal and dead air space between the insert and your window.
		This dead air is essential to noise reduction because anywhere air gets in,
		noise can too. To keep the noise outside, we ensure a precise, snug fit by
		custom making each acoustic window insert using our laser measuring system.
	</p>
	<p>
		Every grade of <a href="/solutions/window-noise-reduction"
			>Indow window inserts reduces outside noise</a
		> by 50% for single-pane windows and our Acoustic Grade does an even better job,
		reducing noise by 70%*. Indow inserts are very easy to install and remove, with
		no damage to your window frames. They not only block outside noise but also prevent
		inside noise from bothering your neighbors.
	</p>
	<p>
		And you don’t have to sacrifice natural light to get the sound reduction you
		want. However, if you want both sound reduction AND reduced light (say you
		get chronic migraines or you want better sleep quality), you can choose
		<a href="/custom-storm-windows/sleep-panel">Indow Sleep Panels</a> instead, which
		block 100% of light and offer superior soundproofing. The best part? You can
		choose which windows you want light-blocking panels and which you want just noise
		blocking for a truly custom experience.
	</p>
	<p>
		While soundproof curtains may be less expensive and easy, they provide
		minimal performance and cut out all natural light while soundproofing is
		occurring. If you want high-quality soundproofing without sacrificing
		natural light, you want Indow inserts.
	</p>
	<p>
		Interested in learning more about Indow window inserts? Check out <a
			href="/">all the insert collections offered by Indow</a
		>
		as a great soundproof curtain alternative, and then
		<a href="/how-to/get-indow-inserts">get a free estimate</a>.
	</p>
	<p class="italic">
		*We want to ensure our inserts help solve your noise problem. Overall noise
		reduction depends on the amount of noise coming through your walls,
		ceilings, floors, doors, and existing windows. Noise reduction will be less
		when placed over double-pane windows. Read our full Noise Reduction Sheet to
		learn more.
	</p>
</Section>
<GetAFreeEstimate title="Let Us Help You">
	{#snippet image()}
		<Image alt="bathroom window ideas for privacy" src={KarenVintageImg} />
	{/snippet}
</GetAFreeEstimate>
<Reviews />
